import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getJob, submitJob } from '@/api/jobs';
import { AiLoading } from '@/components/ui/ai-loading/ai-loading';
import { Button } from '@/components/ui/button/button';
import { Card } from '@/components/ui/card/card';
import { Uploader } from '@/components/ui/uploader/uploader';
import type { Question, StudentAnswers } from '@/types';
import { dueCountdown } from '@/utils/due';

import styles from './submit.module.css';

const isObjective = (t?: string) => t === 'choice' || t === 'fill_blank';

function letterOf(i: number): string {
  return String.fromCharCode(65 + i);
}

export function SubmitPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const jobId = Number(id);
  const [content, setContent] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [answers, setAnswers] = useState<StudentAnswers>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState<string | null>(null);
  const [prefilled, setPrefilled] = useState(false);

  const { data: jobData } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => getJob(jobId),
    enabled: !!jobId,
  });

  const mySub = jobData?.my_submission;
  const job = jobData?.job;
  const questions: Question[] = job?.questions ?? [];
  const objective = isObjective(job?.type);
  const isOverdue = dueCountdown(job?.due_date).level === 'overdue';

  // 学习通模式：已有提交时预填原内容 / 原答案，支持修改后重新提交
  useEffect(() => {
    if (prefilled || !mySub) return;
    if (objective) {
      try {
        const parsed = mySub.content ? JSON.parse(mySub.content) : {};
        if (parsed && typeof parsed === 'object') {
          setAnswers(parsed as StudentAnswers);
        }
      } catch {
        /* 非 JSON，忽略 */
      }
    } else if (mySub.content) {
      setContent(mySub.content);
    }
    setPrefilled(true);
  }, [mySub, prefilled, objective]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const setAnswer = (qid: string, value: string | string[]) => {
    setAnswers((prev) => ({ ...prev, [qid]: value }));
  };

  const validateAnswers = (): string | null => {
    for (const q of questions) {
      const ans = answers[q.id];
      if (q.type === 'fill_blank') {
        const list = Array.isArray(ans) ? ans : [];
        if (list.length === 0 || list.some((a) => !a || !String(a).trim())) {
          return `请完成第 ${questions.indexOf(q) + 1} 题所有填空`;
        }
      } else if (q.type === 'multiple_choice') {
        if (!Array.isArray(ans) || ans.length === 0) {
          return `请选择第 ${questions.indexOf(q) + 1} 题的答案`;
        }
      } else {
        if (typeof ans !== 'string' || !ans) {
          return `请选择第 ${questions.indexOf(q) + 1} 题的答案`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (isOverdue) {
      setError('作业已过截止时间，无法提交；请联系老师延长截止时间');
      return;
    }


    let formData: FormData;
    if (objective) {
      const aErr = validateAnswers();
      if (aErr) {
        setError(aErr);
        return;
      }
      formData = new FormData();
      formData.append('answers', JSON.stringify(answers));
    } else {
      if (!content && !file) {
        setError('请填写内容或上传文件');
        return;
      }
      formData = new FormData();
      if (content) formData.append('content', content);
      if (file) formData.append('file', file);
    }

    setLoading(true);
    try {
      await submitJob(jobId, formData);
      await queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      await queryClient.invalidateQueries({ queryKey: ['jobs'] });
      showToast(mySub ? '修改成功，已重新提交' : '提交成功');
      setTimeout(() => navigate(`/jobs/${jobId}`), 800);
    } catch (err) {
      setError(err instanceof Error ? err.message : '提交失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{mySub ? '修改提交' : '提交作业'}</h1>
      <Card className={styles.card}>
        <AiLoading
          overlay
          active={loading}
          title="AI 正在批改你的作业"
          hint={[
            objective ? '正在核对答案…' : '正在识别作答内容…',
            '正在逐项分析得分点…',
            '正在生成批改报告…',
          ]}
        />
        <form className={styles.form} onSubmit={handleSubmit}>
          {mySub && (
            <p className={styles.notice}>
              你已提交过这份作业，重新提交将覆盖原内容，系统会重新批改。
            </p>
          )}

          {isOverdue && (
            <div className={styles.error}>
              ⏰ 作业已过截止时间，无法提交。如需继续完成，请联系老师延长截止时间。
            </div>
          )}

          {objective ? (
            <div className={styles.questionsWrap}>
              {questions.map((q, idx) => (
                <div key={q.id} className={styles.questionBlock}>
                  <div className={styles.questionStem}>
                    <span className={styles.qIndex}>{idx + 1}.</span>
                    <span>{q.stem}</span>
                    <span className={styles.qScoreTag}>{q.score}分</span>
                  </div>

                  {q.type === 'single_choice' && (
                    <div className={styles.options}>
                      {(q.options ?? []).map((opt, oi) => (
                        <label key={oi} className={styles.optionRow}>
                          <input
                            type="radio"
                            name={`q-${q.id}`}
                            checked={answers[q.id] === letterOf(oi)}
                            onChange={() => setAnswer(q.id, letterOf(oi))}
                          />
                          <span className={styles.optionLetter}>{letterOf(oi)}</span>
                          <span>{opt}</span>
                        </label>
                      ))}
                    </div>
                  )}

                  {q.type === 'multiple_choice' && (
                    <div className={styles.options}>
                      {(q.options ?? []).map((opt, oi) => {
                        const cur = Array.isArray(answers[q.id])
                          ? (answers[q.id] as string[])
                          : [];
                        return (
                          <label key={oi} className={styles.optionRow}>
                            <input
                              type="checkbox"
                              checked={cur.includes(letterOf(oi))}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...cur, letterOf(oi)]
                                  : cur.filter((x) => x !== letterOf(oi));
                                setAnswer(q.id, next);
                              }}
                            />
                            <span className={styles.optionLetter}>{letterOf(oi)}</span>
                            <span>{opt}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {q.type === 'fill_blank' &&
                    (Array.isArray(q.answer) ? q.answer : []).map((_, bi) => {
                      const cur = Array.isArray(answers[q.id])
                        ? (answers[q.id] as string[])
                        : [];
                      return (
                        <div key={bi} className={styles.blankRow}>
                          <span className={styles.blankNo}>第{bi + 1}空</span>
                          <input
                            type="text"
                            className={styles.blankInput}
                            value={cur[bi] ?? ''}
                            onChange={(e) => {
                              const next = [...cur];
                              next[bi] = e.target.value;
                              setAnswer(q.id, next);
                            }}
                            placeholder="请输入答案"
                          />
                        </div>
                      );
                    })}
                </div>
              ))}
            </div>
          ) : (
            <>
              <label className={styles.label}>
                作业内容
                <textarea
                  className={styles.textarea}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  rows={10}
                  placeholder="可直接粘贴作业文字，或上传图片/文件由 OCR 识别"
                />
              </label>
              <div>
                <span className={styles.label}>上传文件（图片优先识别文字）</span>
                <Uploader accept="image/*,.pdf,.doc,.docx" onChange={setFile} value={file} />
              </div>
            </>
          )}

          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.actions}>
            <Button type="button" variant="secondary" onClick={() => navigate(-1)}>
              取消
            </Button>
            <Button type="submit" loading={loading} disabled={isOverdue}>
              {mySub ? '确认修改并提交' : '提交'}
            </Button>
          </div>
        </form>
      </Card>

      {toast && (
        <div className={styles.toast} role="status">
          {toast}
        </div>
      )}
    </div>
  );
}
